import { PERSONALIZATION_AUDIENCE, type PersonalizationAudience } from '../../model/const/personalization';

type DemandbaseCustomFields = Record<string, unknown>;

export interface DemandbaseCompany {
    industry?: unknown;
    sub_industry?: unknown;
    custom_fields?: DemandbaseCustomFields;
    custom_field?: DemandbaseCustomFields;
}

const SEGMENT_FIELDS = ['SEG_1', 'SEG_34', 'SEG_67'];

// The former GTM connector accepted both key names; kept for parity
function getCustomFields(company: DemandbaseCompany): DemandbaseCustomFields {
    return company.custom_fields || company.custom_field || {};
}

export function getDemandbaseSegments(company: DemandbaseCompany): string {
    const custom = getCustomFields(company);

    return SEGMENT_FIELDS
        .map((key) => custom[key])
        .filter((value) => value !== undefined && value !== null && value !== '')
        .join(', ');
}

interface NormalizedCompany {
    industry: string;
    subIndustry: string;
    segment: string;
}

interface PersonalizationRule {
    audience: PersonalizationAudience;
    matches: (company: NormalizedCompany) => boolean;
}

const OT_INDUSTRIES = [
    'manufacturing',
    'oil and gas',
    'energy and environmental',
    'transportation',
    'supply chain and logistics',
    'construction and',
    'mining and metals',
    'building',
];

// Order matters: the first matching rule wins
const RULES: PersonalizationRule[] = [
    {
        audience: PERSONALIZATION_AUDIENCE.SP,
        matches: ({ industry, subIndustry, segment }) => (
            (industry === 'corporate services' && subIndustry === 'it services and consulting') ||
            segment.includes('service provider')
        ),
    },
    {
        audience: PERSONALIZATION_AUDIENCE.CORP_EDUCATION,
        matches: ({ industry }) => industry.includes('schools and education'),
    },
    {
        audience: PERSONALIZATION_AUDIENCE.CORP_HEALTHCARE,
        matches: ({ industry }) => industry.includes('hospitals and healthcare'),
    },
    {
        audience: PERSONALIZATION_AUDIENCE.CORP_PHARMA,
        matches: ({ industry }) => industry.includes('pharma'),
    },
    {
        audience: PERSONALIZATION_AUDIENCE.CORP_TELECOM,
        matches: ({ industry }) => industry.includes('telecommunications'),
    },
    {
        audience: PERSONALIZATION_AUDIENCE.CORP_OT,
        matches: ({ industry }) => OT_INDUSTRIES.some((x) => industry.includes(x)),
    },
];

function normalize(value: unknown): string {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

export function resolveDemandbasePersonalization(company: DemandbaseCompany): PersonalizationAudience | null {
    const normalized: NormalizedCompany = {
        industry: normalize(company.industry),
        subIndustry: normalize(company.sub_industry),
        segment: normalize(getCustomFields(company)['SEG_1']),
    };

    return RULES.find((rule) => rule.matches(normalized))?.audience ?? null;
}
